import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import EvalCard from "@/components/EvalCard";
import TrainingCoach from "@/components/TrainingCoach";
import { ArrowLeft, ArrowRight, Loader2, ShieldCheck, CheckCircle2, XCircle } from "lucide-react";

const STAGE_META = {
  PRE_TEST_AVAILABLE: { pool: "pretest", eyebrow: "Baseline", title: "Pre-Test" },
  TRAINING_AVAILABLE: { pool: "training", eyebrow: "Learn", title: "Training" },
  VETTING_AVAILABLE: { pool: "vetting", eyebrow: "Independent assessment", title: "Vetting" },
  RETEST_AVAILABLE: { pool: "retest", eyebrow: "Fresh assessment", title: "Retest" },
  VETTED: { pool: "alpha", eyebrow: "Project", title: "ALPHA 1.0" },
  ALPHA_AVAILABLE: { pool: "alpha", eyebrow: "Project", title: "ALPHA 1.0" },
};

export default function BengaliPath() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState("");
  const [rationale, setRationale] = useState("");
  const [pasteAttempts, setPasteAttempts] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [started, setStarted] = useState(false);        // instruction gate (pretest/alpha)
  const [honored, setHonored] = useState(false);
  const [feedback, setFeedback] = useState(null);       // training reveal
  const [relearn, setRelearn] = useState(null);
  const [loadError, setLoadError] = useState(null);     // surface API failures instead of infinite loading
  const startRef = useRef(Date.now());

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const r = await api.get("/eval/state");
      setState(r.data);
      setHonored(r.data.honor || false);
      window.scrollTo(0, 0);
    } catch (e) {
      if (e?.response?.status === 403) { toast.error("Your evaluation is not unlocked yet."); navigate("/dashboard"); }
      else { setLoadError(e?.response?.data?.detail || "Could not load your evaluation. Please check your connection and try again."); }
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const retry = () => { setLoadError(null); setLoading(true); load(); };

  const currentItem = state?.items?.find((i) => i.id === state.current_id) || null;
  useEffect(() => { startRef.current = Date.now(); setTick(""); setRationale(""); setPasteAttempts(0); }, [state?.current_id, state?.stage]);

  const meta = state ? STAGE_META[state.stage] : null;
  const pool = meta?.pool;
  const blindPool = pool === "vetting" || pool === "retest";

  const onRationale = (v, isPaste) => { if (isPaste) { setPasteAttempts((p) => p + 1); toast.error("Pasting is disabled during the assessment."); return; } setRationale(v); };

  const submitItem = async () => {
    if (!tick) { toast.error("Please select a judgment."); return; }
    if (!rationale.trim()) { toast.error("Please add a short rationale."); return; }
    setSubmitting(true);
    const body = { item_id: currentItem.id, tick, rationale, ms: Date.now() - startRef.current, paste_attempts: pasteAttempts };
    try {
      const ep = { pretest: "pretest", training: "training", vetting: "vetting", retest: "retest", alpha: "alpha" }[pool];
      const r = await api.post(`/eval/${ep}/submit`, body);
      if (pool === "training") { setFeedback({ ...r.data, item: currentItem }); }
      else { await load(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not submit."); }
    finally { setSubmitting(false); }
  };

  const continueTraining = async () => { setFeedback(null); await load(); };

  const finalize = async (which) => {
    setSubmitting(true);
    try {
      const r = await api.post(`/eval/${which}/finalize`);
      await load();
      if (r.data.integrity_void) toast.error("This attempt was flagged for review. You have one retest opportunity.");
      else if (r.data.vetted) toast.success("Congratulations — you are FIAD Vetted.");
      else toast.message(`Score: ${r.data.score}%`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not finalize."); }
    finally { setSubmitting(false); }
  };

  const acceptHonor = async () => { try { await api.post("/eval/honor", { pool }); setHonored(true); } catch {} };
  const loadRelearn = async () => { try { const r = await api.get("/eval/relearn"); setRelearn(r.data); } catch {} };
  const relearnDone = async () => { try { await api.post("/eval/relearn/done"); setRelearn(null); await load(); } catch {} };

  useEffect(() => { if (state?.stage === "RELEARN_AVAILABLE" && !relearn) loadRelearn(); }, [state?.stage]); // eslint-disable-line

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F6F3EC]"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const shell = (children) => (
    <div className="min-h-screen bg-[#F6F3EC]">
      <div className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button data-testid="back-dash-btn" onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-sm font-medium text-[#525252] hover:text-[#0A0A0A]"><ArrowLeft className="h-4 w-4" /> Dashboard</button>
          <Logo variant="dark" to={null} />
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-6 py-10">{children}</div>
    </div>
  );

  // Visible error state: never leave the user on a perpetual spinner or white screen.
  if (loadError || !state) return shell(
    <div className="text-center py-16" data-testid="eval-error">
      <XCircle className="h-10 w-10 text-[#B42318] mx-auto" strokeWidth={1.5} />
      <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">We couldn't load your evaluation</h1>
      <p className="mt-3 text-[#525252] max-w-md mx-auto">{loadError || "Something went wrong while fetching your progress."}</p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <button data-testid="eval-retry-btn" onClick={retry} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">Try again</button>
        <button onClick={() => navigate("/dashboard")} className="text-sm text-[#525252] underline">Back to dashboard</button>
      </div>
    </div>
  );

  const progressBar = () => {
    const total = state.items?.length || 0;
    const done = state.answered_ids?.length || 0;
    return (
      <div className="mb-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="eyebrow text-[#C79447]">{meta.eyebrow}</span>
          <span className="font-mono-num text-[#525252]" data-testid="eval-progress">{Math.min(done + (currentItem ? 1 : 0), total)} / {total}</span>
        </div>
        <div className="h-1 bg-[#E5E5E5] rounded-full overflow-hidden"><div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${(done / Math.max(1, total)) * 100}%` }} /></div>
        <h1 className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight">{meta.title}</h1>
      </div>
    );
  };

  // ---- terminal / result stages ----
  if (state.stage === "NOT_VETTED_THIS_COHORT") return shell(
    <div className="text-center py-16" data-testid="not-vetted">
      <XCircle className="h-10 w-10 text-[#B42318] mx-auto" strokeWidth={1.5} />
      <h1 className="mt-5 font-display text-3xl font-bold tracking-tight">Not vetted this cohort</h1>
      <p className="mt-3 text-[#525252] max-w-md mx-auto">You did not meet the qualifying standard for this pilot after the retest. Your profile and application remain intact.</p>
      <button onClick={() => navigate("/dashboard")} className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">Back to dashboard</button>
    </div>
  );

  if (state.stage === "RELEARN_AVAILABLE") return shell(
    <div data-testid="relearn">
      <span className="eyebrow text-[#C79447]">Relearn</span>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">Let's revisit a few things.</h1>
      <p className="mt-3 text-[#525252]">{state.vetting?.integrity_void ? "Your last attempt was flagged for review. Review the criteria below, then take your retest." : "Review the criteria you missed, then take your fresh retest."}</p>
      {relearn && (
        <div className="mt-6 space-y-4">
          <p className="text-sm text-[#525252]">Focus criteria: {relearn.missed_criteria.join(", ") || "—"}</p>
          {relearn.items.map((it) => (
            <div key={it.id} className="border border-[#E5E5E5] bg-white rounded-sm p-5">
              <span className="eyebrow text-[#A3A3A3]">{it.target_criterion} — {it.criterion_label}</span>
              <p className="mt-2 text-sm text-[#0A0A0A]">{it.user_request}</p>
              <p className="mt-2 text-sm text-[#525252]">{it.ai_response}</p>
            </div>
          ))}
        </div>
      )}
      <button data-testid="relearn-done-btn" onClick={relearnDone} className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">Start retest <ArrowRight className="h-4 w-4" /></button>
    </div>
  );

  if (state.stage === "VETTED" || state.stage === "ALPHA_AVAILABLE") {
    if (!currentItem && (!state.items || state.items.length === 0 || state.answered_ids?.length >= state.items?.length)) {
      return shell(
        <div className="text-center py-14" data-testid="vetted">
          <div className="inline-flex items-center gap-2 border border-[#C79447] text-[#0A0A0A] px-4 py-2 rounded-sm"><ShieldCheck className="h-5 w-5 text-[#C79447]" /><span className="font-medium">FIAD Vetted — Bengali</span></div>
          <h1 className="mt-6 font-display text-3xl font-bold tracking-tight">You're FIAD Vetted for this pilot.</h1>
          <p className="mt-3 text-[#525252] max-w-md mx-auto">Eligible for internal pilot ALPHA 1.0. Not a job or payment.</p>
          {state.answered_ids?.length >= state.items?.length && state.items?.length > 0 && <p className="mt-2 text-sm text-[#059669]">ALPHA 1.0 work sample submitted. Thank you.</p>}
          <button onClick={() => navigate("/dashboard")} className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">Back to dashboard</button>
        </div>
      );
    }
    // alpha work sample
    return shell(<>
      <div className="mb-6 inline-flex items-center gap-2 border border-[#C79447] text-[#0A0A0A] px-3 py-1.5 rounded-sm text-sm"><ShieldCheck className="h-4 w-4 text-[#C79447]" /> FIAD Vetted — Bengali</div>
      <p className="text-sm text-[#525252] mb-4">Eligible for internal pilot ALPHA 1.0. Not a job or payment.</p>
      {progressBar()}
      <EvalCard item={currentItem} tick={tick} rationale={rationale} onTick={setTick} onRationale={onRationale} />
      <button data-testid="eval-submit-btn" onClick={submitItem} disabled={submitting} className="mt-5 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium disabled:opacity-60">{submitting && <Loader2 className="h-4 w-4 animate-spin" />}Submit & continue <ArrowRight className="h-4 w-4" /></button>
    </>);
  }

  // ---- instruction gate for pretest ----
  if (pool === "pretest" && !started) return shell(
    <div data-testid="pretest-intro">
      <span className="eyebrow text-[#C79447]">Baseline</span>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">Pre-Test</h1>
      <div className="mt-5 space-y-3 text-[#525252] leading-relaxed">
        <p>This is a baseline. <strong className="text-[#0A0A0A]">Pre-Test performance does not affect your FIAD qualification.</strong></p>
        <p>You'll evaluate an AI-generated response against the stated request and a single criterion, then give a short rationale.</p>
        <p>External AI assistance is not permitted — do not use ChatGPT, Gemini, Claude, search engines, another person or external help. Timing and paste signals may be recorded.</p>
        <p>Getting Pre-Test items wrong does not disqualify you.</p>
      </div>
      <button data-testid="start-pretest-btn" onClick={() => setStarted(true)} className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">Begin Pre-Test <ArrowRight className="h-4 w-4" /></button>
    </div>
  );

  // ---- honor gate for vetting/retest ----
  if (blindPool && !honored) return shell(
    <div data-testid="honor-gate">
      <span className="eyebrow text-[#C79447]">{meta.eyebrow}</span>
      <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">This is the independent assessment stage.</h1>
      <div className="mt-5 space-y-3 text-[#525252] leading-relaxed">
        <p>Pass for this path = <strong className="text-[#0A0A0A]">4 of 5 keyed ticks</strong>. Borderline does not count as Pass for the qualification gate.</p>
        <p>There is no coaching, no feedback and no AI assistance during this stage.</p>
      </div>
      <label className="mt-6 flex items-start gap-3 text-sm text-[#0A0A0A]">
        <input data-testid="honor-checkbox" type="checkbox" onChange={(e) => e.target.checked && acceptHonor()} className="mt-1 accent-[#0A0A0A]" />
        <span>I confirm that I will complete this assessment independently and will not use another AI model, another person, or external assistance.</span>
      </label>
    </div>
  );

  // ---- training feedback reveal ----
  if (pool === "training" && feedback) return shell(<>
    {progressBar()}
    <div className="border border-[#E5E5E5] bg-white rounded-sm p-6 sm:p-7" data-testid="training-feedback">
      <div className={`inline-flex items-center gap-2 text-sm font-medium ${feedback.correct ? "text-[#059669]" : "text-[#B42318]"}`}>
        {feedback.correct ? <CheckCircle2 className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
        {feedback.correct ? "Your judgment matched the expected answer." : `Expected judgment: ${feedback.expected_key}`}
      </div>
      <div className="mt-5"><span className="eyebrow text-[#A3A3A3]">Human-reviewed sample reason</span><p className="mt-1.5 text-[#0A0A0A] leading-relaxed">{feedback.sample_rationale}</p></div>
      <div className="mt-4"><span className="eyebrow text-[#A3A3A3]">Feedback</span><p className="mt-1.5 text-[#525252] leading-relaxed">{feedback.training_feedback}</p></div>
      <p className="mt-4 text-xs text-[#A3A3A3]">Rationale quality: {feedback.reason_quality === "reason_ok" ? "clear" : "could be stronger"}</p>
    </div>
    {feedback.coach_enabled && <TrainingCoach itemId={feedback.item.id} />}
    <button data-testid="training-continue-btn" onClick={continueTraining} className="mt-5 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">{feedback.all_done ? "Finish training" : "Continue"} <ArrowRight className="h-4 w-4" /></button>
  </>);

  // ---- blind pool: all answered -> finalize ----
  if (blindPool && !currentItem) return shell(
    <div className="text-center py-14" data-testid="finalize">
      <h1 className="font-display text-2xl font-bold tracking-tight">You've answered every item.</h1>
      <p className="mt-3 text-[#525252]">Submit your {meta.title.toLowerCase()} for scoring. This cannot be changed afterwards.</p>
      <button data-testid="finalize-btn" onClick={() => finalize(pool)} disabled={submitting} className="mt-8 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium disabled:opacity-60">{submitting && <Loader2 className="h-4 w-4 animate-spin" />}Submit {meta.title} <ArrowRight className="h-4 w-4" /></button>
    </div>
  );

  // ---- active item (pretest / training / vetting / retest) ----
  if (!currentItem) return shell(
    <div className="text-center py-16" data-testid="eval-next-error">
      <Loader2 className="h-8 w-8 text-[#A3A3A3] mx-auto" strokeWidth={1.5} />
      <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">Couldn't load the next item</h1>
      <p className="mt-3 text-[#525252] max-w-md mx-auto">This section didn't finish loading. Please try again — your progress is saved.</p>
      <div className="mt-6 flex items-center justify-center gap-3">
        <button data-testid="eval-retry-btn" onClick={retry} className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium">Try again</button>
        <button onClick={() => navigate("/dashboard")} className="text-sm text-[#525252] underline">Back to dashboard</button>
      </div>
    </div>
  );
  return shell(<>
    {progressBar()}
    <EvalCard item={currentItem} tick={tick} rationale={rationale} onTick={setTick} onRationale={onRationale} blockPaste={blindPool} />
    <button data-testid="eval-submit-btn" onClick={submitItem} disabled={submitting} className="mt-5 inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3 rounded-sm text-sm font-medium disabled:opacity-60">
      {submitting && <Loader2 className="h-4 w-4 animate-spin" />}{pool === "training" ? "Submit & reveal" : "Submit & continue"} <ArrowRight className="h-4 w-4" />
    </button>
    {blindPool && <p className="mt-3 text-xs text-[#A3A3A3]">No feedback is shown during this stage. Pasting is disabled.</p>}
  </>);
}
